import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, BirthdayCelebrant } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

const STYLE_LABELS: Record<NonNullable<BirthdayCelebrant['portraitStyle']>, string> = {
  original: 'Original',
  ghibli: '🎨 Ghibli',
  pixar: '🎬 Pixar',
  cartoon: '🖌️ Cartoon',
  watercolor: '💧 Watercolor',
};

function CelebrantCard({ celebrant, index }: { celebrant: BirthdayCelebrant; index: number }) {
  const styleLabel = celebrant.portraitStyle && celebrant.portraitStyle !== 'original'
    ? STYLE_LABELS[celebrant.portraitStyle]
    : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay: index * 0.09, ease: BDAY_EASE }}
      whileHover={{ y: -10, scale: 1.04 }}
      className="flex flex-col items-center gap-3 group"
    >
      {/* avatar */}
      <div className="relative">
        <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
        <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-blue-400/40 shadow-lg shadow-blue-900/60 bg-blue-800/60 flex items-center justify-center">
          {celebrant.imageUrl ? (
            <img
              src={celebrant.imageUrl}
              alt={celebrant.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-4xl">
              {index === 0 ? '⭐' : index % 2 === 0 ? '🎉' : '😊'}
            </span>
          )}
        </div>
        {/* dot badge */}
        <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-blue-400 border-2 border-[#0f172a] flex items-center justify-center">
          <span className="text-[9px]">{index === 0 ? '⭐' : '🎈'}</span>
        </div>
      </div>

      {/* name + relation */}
      <div className="text-center">
        <p className="text-blue-100 font-semibold text-sm group-hover:text-white transition-colors">
          {celebrant.name}
        </p>
        {celebrant.relation && (
          <p className="text-blue-400 text-xs mt-0.5">{celebrant.relation}</p>
        )}
        {styleLabel && (
          <span className="mt-1 inline-block text-[10px] bg-blue-500/20 border border-blue-400/30 text-blue-300 rounded-full px-2 py-0.5">
            {styleLabel}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function CelebrantsSection({ content }: { content: BirthdayContent }) {
  const celebrants = content.celebrants;
  if (!celebrants.length) return null;

  const bg = content.sectionBackgrounds.celebrants;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 100%)' }}
    >
      <SectionBgImage bg={bg} />
      {/* background sparkles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 18 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-blue-400/30"
            style={{ left: `${(i * 5.5) % 100}%`, top: `${(i * 7.3) % 100}%` }}
            animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.5, 1] }}
            transition={{ duration: 2 + (i % 3), repeat: Infinity, delay: (i * 0.15) % 2 }}
          />
        ))}
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* heading */}
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            🎉 The People
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            Celebrants
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-3 text-blue-300 text-sm"
          >
            The amazing people making this day unforgettable
          </motion.p>
        </div>

        {/* grid */}
        <div className="flex flex-wrap justify-center gap-8">
          {celebrants.slice(0, 12).map((c, i) => (
            <CelebrantCard key={`${c.name}-${i}`} celebrant={c} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent"
        />
      </div>
    </section>
  );
}
